import { fetchApi } from "@/lib/api/index.ts";
import {
  devOverridesEnabled,
  useDevOverridesStore,
} from "@/lib/hooks/admin/use-dev-overrides";
import { extractSpotifyTrackId } from "@/lib/mentor-profile/constants";
import {
  getMockReview,
  patchMockDraft,
  submitMockReview,
} from "@/lib/mentor-profile/mock";
import type {
  ReviewSection,
  WizardFurthestStep,
} from "@/lib/mentor-profile/wizard-steps";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type MentorProfileReviewStatus =
  | "invited"
  | "in_progress"
  | "submitted"
  | "in_review"
  | "changes_requested"
  | "approved"
  | "rejected";

export interface MentorProfileDraft {
  first_name?: string;
  last_name?: string;
  display_name?: string;
  email?: string;
  phone?: string;
  birthday?: string;
  sex?: string;
  gender_identity?: string;
  pronouns?: string;
  race?: string;
  location?: string;
  languages?: string[];
  bio?: string;
  favorite_music?: string;
  spotify_preview_url?: string;
  spotify_meta?: Record<string, unknown>;
  profile_picture_url?: string;
  match_card_photo_url?: string;
  profile_audio_url?: string;
  interests?: string[];
  interest_images?: Array<{ interest: string; imageUrl: string }>;
  vibe_images?: Array<{ imageUrl: string; prompt?: string }>;
}

export interface MentorProfileChangeRequest {
  section: ReviewSection;
  note: string;
  status: "open" | "resolved";
  created_at: string;
  created_by: string;
}

export interface MentorProfileReview {
  id: string;
  mentor_id: string;
  status: MentorProfileReviewStatus;
  draft: MentorProfileDraft;
  published_snapshot: MentorProfileDraft | null;
  change_requests: MentorProfileChangeRequest[];
  furthest_step: WizardFurthestStep | string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  review_comment: string | null;
  invite_sent_at: string | null;
  created_at: string;
  updated_at: string;
  mentor?: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    profile_picture_url?: string | null;
  };
}

export const MENTOR_PROFILE_QUERY_KEY = ["mentor-profile", "me"] as const;
export const MENTOR_PROFILE_REVIEWS_QUERY_KEY = [
  "mentor-profile",
  "reviews",
] as const;

type ReviewApiRow = {
  id: string;
  user_id: string;
  draft: MentorProfileDraft;
  published: MentorProfileDraft | null;
  comments: unknown;
  metadata: unknown;
  status: "in_progress" | "in_review" | "changes_requested" | "approved";
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asChangeRequests = (value: unknown): MentorProfileChangeRequest[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const row = asRecord(item);
    if (typeof row.section !== "string" || typeof row.note !== "string") {
      return [];
    }
    return [
      {
        section: row.section as ReviewSection,
        note: row.note,
        status: row.status === "resolved" ? "resolved" : "open",
        created_at: typeof row.created_at === "string" ? row.created_at : "",
        created_by: typeof row.created_by === "string" ? row.created_by : "",
      },
    ];
  });
};

const text = (value: unknown): string | null =>
  typeof value === "string" ? value : null;

export const toMentorProfileReview = (
  row: ReviewApiRow,
): MentorProfileReview => {
  const metadata = asRecord(row.metadata);
  return {
    id: row.id,
    mentor_id: row.user_id,
    status: row.status,
    draft: row.draft ?? {},
    published_snapshot: row.published ?? null,
    change_requests: asChangeRequests(row.comments),
    furthest_step: text(metadata.furthest_step),
    submitted_at: text(metadata.submitted_at),
    reviewed_at: row.approved_at,
    reviewed_by: row.approved_by,
    review_comment: text(metadata.review_comment),
    invite_sent_at: text(metadata.invite_sent_at),
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
};

const isReviewRow = (value: unknown): value is ReviewApiRow => {
  const row = asRecord(value);
  return typeof row.id === "string" && typeof row.user_id === "string";
};

const loadOrCreateMyReview = async (): Promise<MentorProfileReview> => {
  const response = await fetchApi<ReviewApiRow | null>({
    url: "/v1/reviews/me",
    method: "GET",
    params: { type: "profile" },
  });
  if (isReviewRow(response.data)) return toMentorProfileReview(response.data);

  const created = await fetchApi<ReviewApiRow>({
    url: "/v1/reviews/me",
    method: "PATCH",
    data: { type: "profile", draft: {} },
  });
  return toMentorProfileReview(created.data);
};

export const useMentorProfileReview = (enabled = true) => {
  const useMock = useDevOverridesStore(
    (s) => devOverridesEnabled && s.mentorProfileMockData,
  );

  return useQuery({
    queryKey: MENTOR_PROFILE_QUERY_KEY,
    queryFn: async () => {
      if (useMock) return getMockReview();
      return loadOrCreateMyReview();
    },
    enabled,
    refetchOnWindowFocus: false,
  });
};

interface UpdateDraftParams {
  draft: Partial<MentorProfileDraft>;
  furthestStep?: string;
}

type SpotifyEnrichment = Pick<
  MentorProfileDraft,
  "spotify_meta" | "spotify_preview_url"
>;

// Mirrors the server-side `enrichSpotifyDraft` so mock mode can still show real
// Spotify metadata. Uses the public (unauthenticated) endpoint.
const fetchSpotifyEnrichment = async (
  favoriteMusic: string,
): Promise<SpotifyEnrichment> => {
  const trackId = extractSpotifyTrackId(favoriteMusic);
  if (!trackId) {
    return { spotify_meta: undefined, spotify_preview_url: undefined };
  }

  try {
    const { data } = await fetchApi<{
      title: string | null;
      artist: string | null;
      artworkUrl: string | null;
      previewUrl: string | null;
    }>(
      {
        url: `/v2/spotify/track?trackId=${encodeURIComponent(trackId)}`,
        method: "GET",
      },
      false,
    );

    const hasMeta = !!(data.title || data.artist || data.artworkUrl);
    return {
      spotify_meta: hasMeta
        ? {
            title: data.title,
            artist: data.artist,
            artworkUrl: data.artworkUrl,
          }
        : undefined,
      spotify_preview_url: data.previewUrl ?? undefined,
    };
  } catch {
    return {};
  }
};

export const useUpdateMentorProfileDraft = () => {
  const queryClient = useQueryClient();
  const useMock = useDevOverridesStore(
    (s) => devOverridesEnabled && s.mentorProfileMockData,
  );

  return useMutation<MentorProfileReview, Error, UpdateDraftParams>({
    mutationKey: ["mentor-profile", "update-draft"],
    mutationFn: async ({ draft, furthestStep }) => {
      if (useMock) {
        // The rest of the profile stays mocked, but Spotify metadata is public,
        // so enrich it against the real API to preview the "On repeat" card.
        const patch =
          typeof draft.favorite_music === "string"
            ? {
                ...draft,
                ...(await fetchSpotifyEnrichment(draft.favorite_music)),
              }
            : draft;
        return patchMockDraft(patch, furthestStep);
      }
      const response = await fetchApi<ReviewApiRow>({
        url: "/v1/reviews/me",
        method: "PATCH",
        data: {
          type: "profile",
          draft,
          ...(furthestStep
            ? { metadata: { furthest_step: furthestStep } }
            : {}),
        },
      });
      return toMentorProfileReview(response.data);
    },
    onSuccess: (review) => {
      queryClient.setQueryData(MENTOR_PROFILE_QUERY_KEY, review);
    },
  });
};

export interface UploadMentorMediaParams {
  blob: Blob;
  filename: string;
}

const FALLBACK_CONTENT_TYPE = {
  image: "image/jpeg",
  audio: "audio/mpeg",
} as const;

const uploadMentorMedia = async (
  kind: keyof typeof FALLBACK_CONTENT_TYPE,
  { blob }: UploadMentorMediaParams,
  useMock: boolean,
): Promise<string> => {
  if (useMock) return URL.createObjectURL(blob);

  const contentType = blob.type || FALLBACK_CONTENT_TYPE[kind];
  const { data } = await fetchApi<{ key: string; url: string }>({
    url: "/v1/reviews/profile/media/presigned-url",
    method: "GET",
    params: { contentType },
  });

  const put = await fetch(data.url, {
    method: "PUT",
    body: blob,
    headers: { "Content-Type": contentType },
  });
  if (!put.ok) {
    throw new Error(`S3 upload failed: ${put.status}`);
  }

  return data.url.split("?")[0];
};

export const useUploadMentorAudio = () => {
  const useMock = useDevOverridesStore(
    (s) => devOverridesEnabled && s.mentorProfileMockData,
  );

  return useMutation<string, Error, UploadMentorMediaParams>({
    mutationKey: ["mentor-profile", "upload-audio"],
    mutationFn: (params) => uploadMentorMedia("audio", params, useMock),
  });
};

export const useUploadMentorImage = () => {
  const useMock = useDevOverridesStore(
    (s) => devOverridesEnabled && s.mentorProfileMockData,
  );

  return useMutation<string, Error, UploadMentorMediaParams>({
    mutationKey: ["mentor-profile", "upload-image"],
    mutationFn: (params) => uploadMentorMedia("image", params, useMock),
  });
};

export const useSubmitMentorProfile = () => {
  const queryClient = useQueryClient();
  const useMock = useDevOverridesStore(
    (s) => devOverridesEnabled && s.mentorProfileMockData,
  );

  return useMutation<MentorProfileReview, Error, void>({
    mutationKey: ["mentor-profile", "submit"],
    mutationFn: async () => {
      if (useMock) return submitMockReview();
      const response = await fetchApi<ReviewApiRow>({
        url: "/v1/reviews/me/submit",
        method: "POST",
        data: { type: "profile" },
      });
      return toMentorProfileReview(response.data);
    },
    onSuccess: (review) => {
      queryClient.setQueryData(MENTOR_PROFILE_QUERY_KEY, review);
    },
  });
};

export interface MentorInvite {
  inviteUrl: string;
  expiresAt: string;
}

export const useSendMentorInvite = () => {
  return useMutation<MentorInvite, Error, { userId: string }>({
    mutationKey: ["mentor-profile", "send-invite"],
    mutationFn: async ({ userId }) => {
      const response = await fetchApi<MentorInvite>({
        url: "/v1/reviews/profile/invites",
        method: "POST",
        data: { userId },
      });
      return response.data;
    },
  });
};

export interface MentorInviteResolution {
  firstName: string;
  maskedPhone: string;
  alreadyActivated: boolean;
}

export const useResolveMentorInvite = (token: string) => {
  return useQuery({
    queryKey: ["mentor-profile", "invite", token],
    queryFn: async () => {
      const response = await fetchApi<MentorInviteResolution>(
        {
          url: "/v1/reviews/profile/invites",
          method: "GET",
          headers: { "x-invite-token": token },
        },
        false,
      );
      return response.data;
    },
    enabled: token.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useSendMentorInviteOtp = () => {
  return useMutation<{ methodId: string }, Error, { token: string }>({
    mutationKey: ["mentor-profile", "invite", "send-otp"],
    mutationFn: async ({ token }) => {
      const response = await fetchApi<{ methodId: string }>(
        {
          url: "/v1/reviews/profile/invites/otp",
          method: "POST",
          headers: { "x-invite-token": token },
        },
        false,
      );
      return response.data;
    },
  });
};

export const useActivateMentorAccount = () => {
  return useMutation<
    { success: boolean; email: string },
    Error,
    { hash: string; hashedPassword: string }
  >({
    mutationKey: ["mentor-profile", "activate"],
    mutationFn: async ({ hash, hashedPassword }) => {
      const response = await fetchApi<{ success: boolean; email: string }>({
        url: "/v1/reviews/profile/activate",
        method: "POST",
        data: { hash, hashedPassword },
      });
      return response.data;
    },
  });
};

export const useMentorProfileReviewsQueue = (enabled = true) => {
  const useMock = useDevOverridesStore(
    (s) => devOverridesEnabled && s.mentorProfileMockData,
  );

  return useQuery({
    queryKey: MENTOR_PROFILE_REVIEWS_QUERY_KEY,
    queryFn: async () => {
      if (useMock) return [];
      const response = await fetchApi<{ reviews: ReviewApiRow[] }>({
        url: "/v1/reviews",
        method: "GET",
        params: { type: "profile" },
      });
      return response.data.reviews.map(toMentorProfileReview);
    },
    enabled,
    refetchOnWindowFocus: true,
  });
};

export const useMentorProfileReviewById = (
  reviewId: string,
  enabled = true,
) => {
  const useMock = useDevOverridesStore(
    (s) => devOverridesEnabled && s.mentorProfileMockData,
  );

  return useQuery({
    queryKey: [...MENTOR_PROFILE_REVIEWS_QUERY_KEY, reviewId],
    queryFn: async () => {
      if (useMock) return getMockReview();
      const response = await fetchApi<ReviewApiRow>({
        url: `/v1/reviews/${reviewId}`,
        method: "GET",
      });
      return toMentorProfileReview(response.data);
    },
    enabled: enabled && reviewId.length > 0,
    refetchOnWindowFocus: false,
  });
};

export const useMentorProfileReviewByMentorId = (
  mentorId: string,
  enabled = true,
) => {
  return useQuery({
    queryKey: [...MENTOR_PROFILE_REVIEWS_QUERY_KEY, "by-mentor", mentorId],
    queryFn: async () => {
      const response = await fetchApi<ReviewApiRow>({
        url: `/v1/reviews/users/${mentorId}`,
        method: "GET",
        params: { type: "profile" },
      });
      return toMentorProfileReview(response.data);
    },
    enabled: enabled && mentorId.length > 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useApproveMentorProfileReview = () => {
  const queryClient = useQueryClient();

  return useMutation<MentorProfileReview, Error, { reviewId: string }>({
    mutationKey: ["mentor-profile", "approve"],
    mutationFn: async ({ reviewId }) => {
      const response = await fetchApi<ReviewApiRow>({
        url: `/v1/reviews/${reviewId}/approve`,
        method: "POST",
      });
      return toMentorProfileReview(response.data);
    },
    onSuccess: (review) => {
      void queryClient.invalidateQueries({
        queryKey: MENTOR_PROFILE_REVIEWS_QUERY_KEY,
      });
      void queryClient.invalidateQueries({
        queryKey: ["user", review.mentor_id],
      });
      void queryClient.invalidateQueries({
        queryKey: ["profile-sections", review.mentor_id],
      });
    },
  });
};

export const useRequestMentorProfileChanges = () => {
  const queryClient = useQueryClient();

  return useMutation<
    MentorProfileReview,
    Error,
    {
      reviewId: string;
      changes: Array<{ section: ReviewSection; note: string }>;
    }
  >({
    mutationKey: ["mentor-profile", "request-changes"],
    mutationFn: async ({ reviewId, changes }) => {
      const response = await fetchApi<ReviewApiRow>({
        url: `/v1/reviews/${reviewId}/request-changes`,
        method: "POST",
        data: { comments: changes },
      });
      return toMentorProfileReview(response.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: MENTOR_PROFILE_REVIEWS_QUERY_KEY,
      });
    },
  });
};
